/**
 * @module app.core
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { apiCall } from 'app/shared/lib/api/api';
import { IContactLogConfig } from 'app/core/models/essette-interfaces';
import { Toaster } from 'app/shared/lib/toaster';
import { TickService } from 'app/shared/services/event-services';
import { ContactLogService } from 'app/core/services/contact-log-service';
import { DateTimeValue } from 'app/shared/lib/date-time';
import { forceNull } from 'app/shared/lib/utilities';
import * as Core from 'app/core/models/essette-api';
import * as CD from 'app/cd/models/essette-api';
import * as UM from 'app/um/models/essette-api';
import * as CM from 'app/cm/models/essette-api';
import { getReference } from 'app/shared/models/essette-types';
import { I18N } from 'aurelia-i18n';


@autoinject
export class SaveContactLog extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable.objectNull
    protected contactLog: Core.ContactLog | null = null;

    @bindable({ defaultBindingMode: bindingMode.toView })
    protected selectedMethod: CM.CaseContactLogMethod | UM.AuthContactLogMethod | null = null;

    @bindable.objectNull
    protected selectedOutcome: CM.CaseContactLogOutcome | UM.AuthContactLogOutcome | null = null;

    protected selectedContacts: Array<CD.MemberContactComposite> = [];

    @bindable({ defaultBindingMode: bindingMode.toView })
    public memberId: string | null = null;

    @bindable({ defaultBindingMode: bindingMode.toView })
    public taskId: string | null = null;

    @bindable({ defaultBindingMode: bindingMode.toView })
    public targetId: string | null = null;

    @bindable({ defaultBindingMode: bindingMode.oneTime })
    public contactLogConfig: IContactLogConfig = {};

    @bindable({ defaultBindingMode: bindingMode.toView })
    protected methodReadonly: boolean = false;

    @bindable({ defaultBindingMode: bindingMode.toView })
    protected methods: ReadonlyArray<CM.CaseContactLogMethod | UM.AuthContactLogMethod> = [];
    protected outcomes: ReadonlyArray<CM.CaseContactLogOutcome | UM.AuthContactLogOutcome> = [];

    @bindable({ defaultBindingMode: bindingMode.toView })
    protected contacts: ReadonlyArray<CD.MemberContact> = [];

    protected overrideContactedDate: boolean = false;
    protected maxTextLength: number = 4000;


    ////****************** Element References ***************************************////


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly contactLogService: ContactLogService,
        protected readonly ticker: TickService,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        this.contactLogService.configure(this.contactLogConfig);
    }


    ////****************** Events ***************************************************////
    protected selectedMethodChanged(): void {
        this.selectedOutcome = null;
        this.outcomes = [];
        if(this.selectedMethod?.outcomes) this.outcomes = this.selectedMethod.outcomes;
        if(this.outcomes?.length === 1) this.selectedOutcome = this.outcomes[0];
    }


    ////****************** Functions ************************************************////
    protected hasOutcomes: (method: CM.CaseContactLogMethod | UM.AuthContactLogMethod) => boolean =
        (method: CM.CaseContactLogMethod | UM.AuthContactLogMethod) => {
            if(!method.outcomes) return false;
            return method.outcomes.length > 0;
        };

    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    protected isValidOverrideDate(value: string): boolean {
        if(!value) { return true; }

        // check that the override date is not in the future
        return new DateTimeValue(this.env.now()).isAfter(new DateTimeValue(value));
    }

    public reset(): void {
        this.contactLog = null;
        this.selectedContacts = [];
        if(!this.methodReadonly) {
            this.outcomes = [];
            this.selectedMethod = null;
        }
        this.selectedOutcome = null;
        this.overrideContactedDate = false;
    }

    public submittedChanges?: any;
    public async submit(): Promise<boolean> {
        if(await this.saveContactLog()) {
            this.submittedChanges = this.contactLog;
            return true;
        }
        return false;
    }

    protected buildContactLog(): CM.CaseContactLog & UM.AuthContactLog {
        const contactLog: CM.CaseContactLog & UM.AuthContactLog = {
            method: getReference(this.selectedMethod),
            outcome: getReference(this.selectedOutcome),
            summary: forceNull(this.contactLog?.summary),
            contacted: {
                dateTimeRange: {
                    start: this.overrideContactedDate ? this.contactLog?.contacted?.dateTimeRange?.start : this.env.now()
                }
            },
            contacts: [],
            metadata: {
                created: {
                    byTask: this.taskId ? {
                        id: this.taskId
                    } : undefined
                }
            }
        };

        this.selectedContacts.forEach(contact => {
            const contactReference: CD.MemberContactLogContactTargetModel = {
                id: contact.memberContact?.relatedMember?.id || contact.memberProvider?.provider?.id || contact.memberContact?.id,
                type: {
                    code: contact.memberContact?.relatedMember ? 'CD.Member' : contact.memberContact ? 'CD.MemberContact' : 'CD.Provider'
                }
            }
            if(contact.memberContact?.relationship?.id || contact.memberProvider?.relationship?.id) {
                contactReference.relationships = [
                    {
                        id: contact.memberContact?.relationship?.id || contact.memberProvider?.relationship?.id,
                        type: {
                            code: contact.memberProvider ? 'CD.MemberProviderRelationship' : 'CD.MemberRelationship'
                        }
                    }
                ]
            }
            contactLog.contacts?.push(contactReference);
        });

        if(this.contactLogConfig.targetType == 'CM.Case') {
            contactLog.case = { id: this.targetId };
        } else if(this.contactLogConfig.targetType == 'UM.Auth') {
            contactLog.auth = { id: this.targetId };
        }

        return contactLog;
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async saveContactLog(): Promise<boolean> {
        let success = false;
        try {
            this.state.beginSave();

            const response = await this.contactLogService.saveContactLog(this.buildContactLog());
            success = response.isSuccessful;
        }
        catch(error) {
            throw error;
        }
        finally {
            let message: string = '';

            if(success) {
                message = this.i18n.tr('ui:messages.events.saved', { what: this.i18n.tr('ui:labels.record', { what: this.i18n.tr('core:contactLog.$') }) })
                this.toaster.info().message(message).show();
            } else {
                message = this.i18n.tr('ui:messages.events.saveFailed', { what: this.i18n.tr('ui:labels.record', { what: this.i18n.tr('core:contactLog.$') }) })
                this.toaster.error().message(message).show();
            }

            this.state.endSave();
            return success;
        }
    }
}
